import { supabase } from '../../shared/supabase/supabaseClient';
import { createKeyedTtlCache } from '../../shared/data/keyedTtlCache';
import { registerSessionCacheClearHandler } from '../../shared/ports/sessionResetPort';

export type SupervisorPinAction =
  | { module: 'POS'; action: 'void' | 'discount' }
  | { module: 'Cash'; action: 'void' | 'correct' }
  | { module: 'Fiado'; action: 'credit_override' };

export function mapSupervisorPinError(message: string): string {
  if (message.includes('supervisor_pin_invalid')) return 'PIN incorrecto.';
  if (message.includes('supervisor_pin_not_configured')) {
    return 'Esta sucursal no tiene PIN de supervisor configurado. Configuralo en Ajustes → Reglas del POS.';
  }
  if (message.includes('supervisor_pin_required')) return 'Ingresá el PIN de supervisor.';
  if (message.includes('supervisor_override_required')) return 'La autorización con PIN es obligatoria para esta acción.';
  if (message.includes('branch_id_required')) return 'No hay una sucursal activa seleccionada.';
  if (message.includes('not_authenticated')) return 'La sesión expiró. Volvé a iniciar sesión.';
  if (message.includes('supervisor_pin_invalid_length') || message.includes('supervisor_pin_digits_only')) {
    return 'El PIN debe ser numérico, de 4 a 12 dígitos.';
  }
  if (message.includes('supervisor_override_invalid')) {
    return 'La autorización expiró. Pedí el PIN de nuevo.';
  }
  if (message.includes('module_permission_denied')) {
    return 'Sin permiso para esta acción.';
  }
  return message || 'No se pudo autorizar con PIN.';
}

// Los lectores de UI (detalle de venta, caja, ajustes) preguntan por mount si la
// sucursal tiene PIN. Cambia solo vía set/clear (abajo); el enforce real sigue
// server-side en los commands, así que un TTL corto es seguro.
const pinPresenceCache = createKeyedTtlCache(60_000);

export async function branchHasSupervisorPin(
  branchId: string,
  opts?: { force?: boolean }
): Promise<boolean> {
  return pinPresenceCache.get(
    branchId,
    async () => {
      const { data, error } = await supabase.rpc('branch_has_supervisor_pin', {
        p_branch_id: branchId,
      });
      if (error) throw new Error(mapSupervisorPinError(error.message));
      return data === true;
    },
    { force: opts?.force }
  );
}

export async function requireBranchSupervisorPin(branchId: string): Promise<void> {
  // Gate previo a una acción sensible: consulta fresca, no el cache de UI.
  if (!(await branchHasSupervisorPin(branchId, { force: true }))) {
    throw new Error('Esta sucursal no tiene PIN de supervisor configurado. Configuralo en Ajustes → Reglas del POS.');
  }
}

export async function setBranchSupervisorPin(branchId: string, pin: string): Promise<void> {
  const { error } = await supabase.rpc('set_branch_supervisor_pin', {
    p_branch_id: branchId,
    p_pin: pin,
  });
  if (error) throw new Error(mapSupervisorPinError(error.message));
  pinPresenceCache.set(branchId, true);
}

export async function clearBranchSupervisorPin(branchId: string): Promise<void> {
  const { error } = await supabase.rpc('clear_branch_supervisor_pin', {
    p_branch_id: branchId,
  });
  if (error) throw new Error(mapSupervisorPinError(error.message));
  pinPresenceCache.set(branchId, false);
}

registerSessionCacheClearHandler(() => pinPresenceCache.clear());

/** Verify PIN once; returns override id to attach to the sensitive RPC payload (not the PIN). */
export async function authorizeSupervisorPin(params: {
  branchId: string;
  pin: string;
  module: string;
  action: string;
  context?: Record<string, unknown>;
}): Promise<string> {
  const { data, error } = await supabase.rpc('verify_supervisor_pin_override', {
    p_branch_id: params.branchId,
    p_pin: params.pin,
    p_module: params.module,
    p_action: params.action,
    p_context: params.context ?? {},
  });
  if (error) throw new Error(mapSupervisorPinError(error.message));
  const id = typeof data === 'string' ? data : data != null ? String(data) : '';
  if (!id) throw new Error('No se pudo autorizar con PIN.');
  return id;
}
